import moment from 'moment';
import { getLocale } from 'umi';
import { formatReleaseTime } from '@home/components/NavHeader/sreachContext/utils';
import { isNotEmpty, getValueByKeys } from '@intelligence/v3/utils/common';
import { getCountryNameByCountryCode } from '@intelligence/utils/commonUtils';
import {
  getAlineaDateValue,
  getAlineaGenreTextFromItem,
  getAlineaMetricText,
  getAlineaOptionText,
  getAlineaPlatformText,
  getAlineaPublisherClassText,
  getAlineaRegionText,
  getAlineaSourceQualityText,
  getAlineaValue,
  getMetricLabel,
} from '@intelligence/v3/pages/TopCharts/PCConsoleGames/AlineaAnalyticsCalibration/utils';
import { buildGenreConfigs } from './common';

const getSummaryRegions = (params?: IRecord | null, allParams?: IRecord | null): string[] => {
  const regions = params?.regions || allParams?.regions || params?.markets || allParams?.markets;
  if (Array.isArray(regions)) {
    return regions;
  }

  return [];
};

const getSummaryRegionText = (params?: IRecord | null, allParams?: IRecord | null): string => {
  const regionOptions = allParams?.regionOptions || params?.regionOptions || [];
  const locale = getLocale();

  return getSummaryRegions(params, allParams)
    .map((region) => {
      return getAlineaOptionText(regionOptions, region, locale, region);
    })
    .filter(isNotEmpty)
    .join(', ');
};

const getSummaryMetrics = (params?: IRecord | null, allParams?: IRecord | null): string[] => {
  const sourceMetrics = params?.source_metrics || allParams?.source_metrics || {};
  const alineaMetrics = sourceMetrics?.alinea_calibration;
  if (Array.isArray(alineaMetrics)) {
    return alineaMetrics;
  }

  const metrics = params?.metrics || allParams?.metrics;
  if (Array.isArray(metrics)) {
    return metrics;
  }

  return [];
};

const getSummaryMetricText = (params?: IRecord | null, allParams?: IRecord | null): string => {
  const locale = getLocale();

  return getSummaryMetrics(params, allParams)
    .map((metric) => {
      return getMetricLabel(metric, locale);
    })
    .filter(isNotEmpty)
    .join(', ');
};

const getCountryName = (countryCode: string, regionsCountries: IRegion[]): string => {
  if (!countryCode) {
    return '';
  }

  const candidateCodes = Array.from(new Set([countryCode, countryCode.toLowerCase(), countryCode.toUpperCase()]));
  for (const candidateCode of candidateCodes) {
    const countryName = getCountryNameByCountryCode(regionsCountries as any, candidateCode);
    if (countryName && countryName !== candidateCode) {
      return countryName;
    }
  }

  return countryCode;
};

const formatDateValue = (item: IRecord, params?: IRecord | null, allParams?: IRecord | null): string => {
  const dateValue = getAlineaDateValue(item);
  if (!dateValue) {
    return '';
  }

  const date = moment(dateValue);
  if (!date.isValid()) {
    return dateValue;
  }

  const dateType = params?.date_type || allParams?.date_type;
  if (dateType === 'monthly') {
    return date.format('YYYY-MM');
  }

  return date.format('YYYY-MM-DD');
};

const buildAlineaGenreConfigs = (params?: IRecord | null): IAlineaDownloadHeader[] => {
  const genreConfigs = buildGenreConfigs(params) as IAlineaDownloadHeader[];

  return genreConfigs
    .filter((config) => {
      return !config.when || config.when();
    })
    .map((config) => {
      if (typeof config?.key !== 'string') {
        return config;
      }

      const { key } = config;
      return {
        ...config,
        key: (item: IRecord) => {
          return getAlineaGenreTextFromItem({
            item,
            key,
            genreOptionsMap: params?.genreOptionsMap,
            genreSource: params?.genre_source || 'iegg',
            locale: getLocale(),
            emptyText: '',
          });
        },
      };
    });
};

const getSortValue = (item: IRecord, orderMetric: string): string | number => {
  if (orderMetric === 'date') {
    const dateValue = getAlineaDateValue(item);
    const date = moment(dateValue);
    if (date.isValid()) {
      return date.valueOf();
    }

    return dateValue;
  }

  if (orderMetric === 'value') {
    const value = getAlineaValue(item);
    const numberValue = Number(value);
    if (!Number.isNaN(numberValue)) {
      return numberValue;
    }

    return '';
  }

  return getValueByKeys(item, [orderMetric]) || '';
};

const sortRows = (rows: IRecord[], params?: IRecord | null, allParams?: IRecord | null): IRecord[] => {
  const orderMetric = params?.order_metric || allParams?.order_metric || '';
  if (!orderMetric) {
    return rows;
  }

  const order = params?.order || allParams?.order || 'desc';
  const direction = order === 'asc' ? 1 : -1;

  return [...rows].sort((left, right) => {
    const leftValue = getSortValue(left, orderMetric);
    const rightValue = getSortValue(right, orderMetric);
    const leftEmpty = leftValue === '';
    const rightEmpty = rightValue === '';
    if (leftEmpty && rightEmpty) {
      return 0;
    }
    if (leftEmpty) {
      return 1;
    }
    if (rightEmpty) {
      return -1;
    }

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      return (leftValue - rightValue) * direction;
    }

    return String(leftValue).localeCompare(String(rightValue)) * direction;
  });
};

const getRows = (data?: IRecord | null, params?: IRecord | null, allParams?: IRecord | null): IRecord[] => {
  const list = data?.list || data?.data?.list || [];
  if (!Array.isArray(list)) {
    return [];
  }

  return sortRows(list, params, allParams);
};

interface IAlineaDownloadHeader {
  title: string;
  key: string | ((item: IRecord) => string | number);
  when?: () => boolean;
}

const buildSheetData = (headers: IAlineaDownloadHeader[], rows: IRecord[]): Array<Array<string | number>> => {
  return [
    headers.map((header) => header.title),
    ...rows.map((row) => {
      return headers.map((header) => {
        if (typeof header.key === 'function') {
          return header.key(row);
        }

        return getValueByKeys(row, [header.key]) || '';
      });
    }),
  ];
};

const getHeaders = (params?: IRecord | null, allParams?: IRecord | null): IAlineaDownloadHeader[] => {
  const mergedParams = {
    ...allParams,
    ...params,
  };
  const trueDataHeaderLabels = mergedParams?.trueDataHeaderLabels as IRecord | undefined;
  const locale = getLocale();
  const regionOptions = allParams?.regionOptions || params?.regionOptions || [];
  const regionsCountries = allParams?.regionsCountries || params?.regionsCountries || [];
  const genreConfigs = buildAlineaGenreConfigs(mergedParams);
  let companyHeaders: IAlineaDownloadHeader[] = [];
  if (params?.company_show === 1) {
    companyHeaders = [
      {
        title: 'Publisher Company Name',
        key: 'publisher',
      },
      {
        title: 'Publisher Company HQ Name',
        key: (item: IRecord) => getCountryName(item?.publisher_addr || '', regionsCountries),
      },
      {
        title: 'Publisher Parent Company',
        key: 'parent_publisher',
      },
      {
        title: 'Developer Company Name',
        key: 'developer',
      },
      {
        title: 'Developer Company HQ Name',
        key: (item: IRecord) => getCountryName(item?.developer_addr || '', regionsCountries),
      },
      {
        title: 'Publisher Class',
        key: (item: IRecord) => getAlineaPublisherClassText(String(item?.publisher_class || ''), locale, ''),
      },
    ];
  }

  let releaseDateHeaders: IAlineaDownloadHeader[] = [];
  if (params?.release_date_show === 1) {
    releaseDateHeaders = [
      {
        title: trueDataHeaderLabels?.release || 'Release Date',
        key: (item: IRecord) => {
          return formatReleaseTime(
            locale,
            {
              isSpecialReleaseTime: item?.is_special_release_time,
              releaseTime: item?.release_time,
              releaseTimeEN: item?.release_time_en,
              releaseTimeCN: item?.release_time_cn,
            },
            '',
          );
        },
      },
    ];
  }

  return [
    {
      title: trueDataHeaderLabels?.gameName || 'Game Name',
      key: 'name',
    },
    ...companyHeaders,
    ...releaseDateHeaders,
    {
      title: trueDataHeaderLabels?.date || 'Date',
      key: (item: IRecord) => formatDateValue(item, mergedParams),
    },
    ...genreConfigs,
    {
      title: trueDataHeaderLabels?.platform || 'Platform',
      key: (item: IRecord) => getAlineaPlatformText(item, ''),
    },
    {
      title: trueDataHeaderLabels?.region || 'Region',
      key: (item: IRecord) => getAlineaRegionText(item, regionOptions, locale, ''),
    },
    {
      title: trueDataHeaderLabels?.source || 'Source',
      key: 'source',
    },
    {
      title: trueDataHeaderLabels?.sourceQuality || 'Source Quality',
      key: (item: IRecord) => getAlineaSourceQualityText(item?.source_quality, locale, ''),
    },
    {
      title: trueDataHeaderLabels?.metrics || 'Metrics',
      key: (item: IRecord) => getAlineaMetricText(item, locale, ''),
    },
    {
      title: trueDataHeaderLabels?.value || 'Value',
      key: getAlineaValue,
    },
  ];
};

export const getAlineaAnalyticsCalibrationPCConsoleTopChartsConfig = (
  data?: IRecord | null,
  params?: IRecord | null,
  allParams?: IRecord | null,
) => {
  const rows = getRows(data, params, allParams);
  const headers = getHeaders(params, allParams);

  return {
    stat: {
      sheetName: 'Stats',
      style: 'normal',
      data: buildSheetData(headers, rows),
    },
    summary: {
      sheetName: 'Summary',
      style: 'none',
      data: [
        ['Date Start', params?.start_date || ''],
        ['Date End', params?.end_date || ''],
        ['Region', getSummaryRegionText(params, allParams)],
        ['Metrics', getSummaryMetricText(params, allParams)],
      ],
    },
  };
};
